import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Alert, Keyboard, StyleSheet, View } from 'react-native';
import { Button, SettingsSection, SettingsItemInput } from 'design-system';
import { IconHome, IconUnion, IconLocation, IconPostageStamp, } from 'design-system/icons';
import { IconRoad } from 'design-system/icons/IconRoad';
import { IconGlobeGradient } from 'design-system/icons/IconGlobeGradient';
import { validateUKPostcode } from 'utils/validation';
import { useKeyboardAdjustPan } from 'hooks/useKeyboardAdjustPan';
import { useMainStackNavigation } from 'utils/types/navigationV6';
const strings = {
    defaultTitle: 'Choose..',
};
const getIsBuildingNameInvalid = (buildingName) => {
    if (buildingName?.length < 2) {
        return true;
    }
    const hasAlphabet = /[a-zA-Z]/.test(buildingName);
    return !hasAlphabet;
};
const AddressInput = React.forwardRef(({ defaultAddress, onSubmitEditing, noExteriorView, postCodeHandled }, ref) => {
    const navigation = useMainStackNavigation();
    const [state, setState] = useState({
        buildingName: '',
        buildingNumber: '',
        subBuildingName: '',
        postcode: '',
        streetName: '',
        townOrCity: '',
        county: '',
    });
    useKeyboardAdjustPan();
    const postCodeRef = useRef(null);
    const flatNumberRef = useRef(null);
    const buildingNameRef = useRef(null);
    const buildingNumberRef = useRef(null);
    const streetRef = useRef(null);
    const townRef = useRef(null);
    const countyNameRef = useRef(null);
    useEffect(() => {
        const address = defaultAddress;
        if (address) {
            const { buildingName, buildingNumber, subBuildingName, streetName, townOrCity, county, postcode, } = address;
            setState((oldState) => ({
                ...oldState,
                postcode: postcode || '',
                buildingName: buildingName || '',
                buildingNumber: buildingNumber || '',
                county: county || '',
                streetName: streetName || '',
                subBuildingName: subBuildingName || '',
                townOrCity: townOrCity || '',
            }));
        }
    }, [defaultAddress]);
    const onPressFind = useCallback(() => {
        Keyboard.dismiss();
        const { postcode: untrimmedPostcode } = state;
        const postcode = untrimmedPostcode.trim();
        const isValidUKPostCode = validateUKPostcode(postcode);
        if (isValidUKPostCode) {
            if (postCodeHandled) {
                flatNumberRef.current?.focus();
            }
            else {
                navigation.navigateDeprecated('FindMyAddress', {
                    postcode,
                    onAddressSelected: (locationInfo) => {
                        const { buildingName, buildingNumber, county, streetName, subBuildingName, townOrCity, } = locationInfo;
                        setState((oldState) => ({
                            ...oldState,
                            buildingName: buildingName || '',
                            buildingNumber: buildingNumber || '',
                            county: county || '',
                            streetName: streetName || '',
                            subBuildingName: subBuildingName || '',
                            townOrCity: townOrCity || '',
                        }));
                    },
                });
            }
        }
        else {
            return Alert.alert('Hold on 💫', 'You need to enter a valid postcode.');
        }
        return null;
    }, [postCodeHandled, state]);
    const onSetPostCode = useCallback((postcode) => {
        setState((oldState) => ({
            ...oldState,
            postcode: postcode.toUpperCase(),
        }));
    }, []);
    const onSetCity = useCallback((townOrCity) => {
        setState((oldState) => ({ ...oldState, townOrCity }));
    }, []);
    const onSetCounty = useCallback((county) => {
        setState((oldState) => ({ ...oldState, county }));
    }, []);
    const onSetFlatNumber = useCallback((subBuildingName) => {
        setState((oldState) => ({ ...oldState, subBuildingName }));
    }, []);
    const onSetBuildingName = useCallback((buildingName) => {
        setState((oldState) => ({ ...oldState, buildingName }));
    }, []);
    const onSetBuildingNumber = useCallback((buildingNumber) => {
        setState((oldState) => ({ ...oldState, buildingNumber }));
    }, []);
    const onSetStreetName = useCallback((streetName) => {
        setState((oldState) => ({ ...oldState, streetName }));
    }, []);
    const validateInputs = useCallback(async () => {
        Keyboard.dismiss();
        const { buildingName, buildingNumber, subBuildingName, streetName, townOrCity, county, postcode: untrimmedPostcode, } = state;
        const postcode = untrimmedPostcode.trim();
        const postcodes = validateUKPostcode(postcode);
        if (!postcode || !postcodes)
            return Alert.alert('Hold on 💫', 'You need to enter a valid postcode.', [{ onPress: () => postCodeRef.current?.focus(), text: 'OK' }]);
        if (buildingName) {
            if (getIsBuildingNameInvalid(buildingName)) {
                return Alert.alert('Hold on 💫', 'You need to enter a valid building name.', [{ onPress: () => buildingNameRef.current?.focus(), text: 'OK' }]);
            }
        }
        if (!buildingNumber) {
            const hasBuildingNumber = await new Promise((resolve) => {
                Alert.alert('Hold on 💫', 'Continue without a building/street number?', [
                    {
                        onPress: () => {
                            resolve(false);
                        },
                        text: 'Continue',
                    },
                    {
                        onPress: () => {
                            buildingNumberRef.current?.focus();
                            resolve(true);
                        },
                        text: 'Edit',
                    },
                ]);
            });
            if (hasBuildingNumber) {
                return undefined;
            }
        }
        if (!streetName || streetName.length < 2)
            return Alert.alert('Hold on 💫', 'You need to set your street name.', [
                { onPress: () => streetRef.current?.focus(), text: 'OK' },
            ]);
        if (!townOrCity)
            return Alert.alert('Hold on 💫', 'You need to set your town or city.', [
                { onPress: () => townRef.current?.focus(), text: 'OK' },
            ]);
        if (!county)
            return Alert.alert('Hold on 💫', 'You need to set your county.', [
                { onPress: () => countyNameRef.current?.focus(), text: 'OK' },
            ]);
        const body = {
            county,
            postcode,
            streetName,
            townOrCity,
            isPrimary: true,
            buildingName: '',
            buildingNumber: '',
            subBuildingName: '',
            countryIsoCode: 'GB',
        };
        if (buildingNumber)
            body.buildingNumber = buildingNumber;
        if (buildingName)
            body.buildingName = buildingName;
        if (subBuildingName)
            body.subBuildingName = subBuildingName;
        return body;
    }, [state]);
    useImperativeHandle(ref, () => ({ validateInputs }));
    const Wrapper = useMemo(() => {
        if (noExteriorView) {
            return View;
        }
        return SettingsSection;
    }, [noExteriorView]);
    return (<Wrapper noMarginTop>
        <SettingsItemInput ref={postCodeRef} autoCapitalize="characters" returnKeyType={postCodeHandled ? 'next' : 'search'} maxLength={12} icon={<IconPostageStamp />} description="Postcode" value={state.postcode} onChangeText={onSetPostCode} blurOnSubmit={false} onClearTextInput={() => {
            onSetPostCode('');
        }} placeholder={strings.defaultTitle} onSubmitEditing={onPressFind} autoComplete="postal-code"/>
        {!postCodeHandled && (<Button style={styles.findButton} gray title="Find my address" onPress={onPressFind}/>)}
        <SettingsItemInput maxLength={50} icon={<IconUnion />} ref={flatNumberRef} description="Flat number (optional)" value={state.subBuildingName} onChangeText={onSetFlatNumber} onClearTextInput={() => {
            onSetFlatNumber('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => buildingNameRef.current?.focus()}/>
        <SettingsItemInput maxLength={50} ref={buildingNameRef} icon={<IconHome gradientKey="orange"/>} description="Building name (optional)" value={state.buildingName} onChangeText={onSetBuildingName} onClearTextInput={() => {
            onSetBuildingName('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => buildingNumberRef.current?.focus()}/>
        <SettingsItemInput maxLength={10} ref={buildingNumberRef} icon={<IconHome gradientKey="apricot"/>} description="Building number / Street number" value={state.buildingNumber} onChangeText={onSetBuildingNumber} onClearTextInput={() => {
            onSetBuildingNumber('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => streetRef.current?.focus()}/>
        <SettingsItemInput maxLength={50} ref={streetRef} icon={<IconRoad />} description="Street" value={state.streetName} onChangeText={onSetStreetName} onClearTextInput={() => {
            onSetStreetName('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => townRef.current?.focus()} autoCapitalize="words"/>
        <SettingsItemInput maxLength={50} ref={townRef} icon={<IconLocation gradientKey="lightPink"/>} description="Town / City " value={state.townOrCity} onChangeText={onSetCity} onClearTextInput={() => {
            onSetCity('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => countyNameRef.current?.focus()} autoCapitalize="words"/>
        <SettingsItemInput maxLength={50} ref={countyNameRef} icon={<IconGlobeGradient gradientKey="darkPink"/>} description="County / Region" value={state.county} onChangeText={onSetCounty} onClearTextInput={() => {
            onSetCounty('');
        }} placeholder={strings.defaultTitle} onSubmitEditing={onSubmitEditing} returnKeyType="done" autoCapitalize="words"/>
      </Wrapper>);
});
const styles = StyleSheet.create({
    findButton: {
        margin: 20,
    },
});
export default AddressInput;
